/**
 * MobileControls - Coordinator for all mobile control components
 *
 * Combines VirtualJoystick and MobileActionButtons into a single
 * interface that can be easily integrated with the game.
 */

import { Container } from 'pixi.js';
import { VirtualJoystick, type JoystickState } from './VirtualJoystick';
import { MobileActionButtons } from './MobileActionButtons';
import { MobileDeviceManager } from '../utils/MobileDetection';
import { MobileSettings } from '../utils/MobileSettings';
import type { MobileInputState } from '../network/InputManager';

/**
 * Mobile controls configuration
 */
export interface MobileControlsConfig {
  /** Whether to auto-detect mobile and show controls */
  autoShow: boolean;
}

const DEFAULT_CONFIG: MobileControlsConfig = {
  autoShow: true,
};

/**
 * MobileControls - Unified mobile control interface
 */
export class MobileControls {
  /** Main container */
  public container: Container;

  /** Virtual joystick */
  private _joystick: VirtualJoystick;

  /** Action buttons */
  private _buttons: MobileActionButtons;

  /** Configuration */
  private _config: MobileControlsConfig;

  /** Whether controls are active */
  private _active: boolean = false;

  /** Canvas element */
  private _canvas: HTMLCanvasElement | null = null;

  /** State change callback */
  private _onStateChange: ((state: MobileInputState) => void) | null = null;

  /** Current combined state */
  private _state: MobileInputState = {
    active: false,
    joystickAngle: 0,
    joystickForce: 0,
    sprinting: false,
    crawling: false,
  };

  constructor(config: Partial<MobileControlsConfig> = {}) {
    this._config = { ...DEFAULT_CONFIG, ...config };

    // Create container
    this.container = new Container();
    this.container.label = 'mobile-controls';

    // Create joystick
    this._joystick = new VirtualJoystick();
    this.container.addChild(this._joystick.container);

    // Create action buttons
    this._buttons = new MobileActionButtons();
    this.container.addChild(this._buttons.container);

    // Setup callbacks
    this._joystick.onStateChange((joystickState) => {
      this._updateState(joystickState);
    });

    this._buttons.onStateChange((sprinting, crawling) => {
      this._state.sprinting = sprinting;
      this._state.crawling = crawling;
      this._notifyStateChange();
    });

    // Initially hidden
    this.container.visible = false;
  }

  /**
   * Update combined state from joystick
   */
  private _updateState(joystickState: JoystickState): void {
    this._state.active = joystickState.active || this._state.sprinting || this._state.crawling;
    this._state.joystickAngle = joystickState.angle;
    this._state.joystickForce = joystickState.force;
    this._notifyStateChange();
  }

  /**
   * Notify state change
   */
  private _notifyStateChange(): void {
    if (this._onStateChange) {
      this._onStateChange(this._state);
    }
  }

  /**
   * Start mobile controls
   */
  start(canvas: HTMLCanvasElement): void {
    if (this._active) return;

    this._canvas = canvas;
    this._active = true;

    // Start listening for touch events
    this._joystick.start(canvas);
    this._buttons.start(canvas);

    // Show controls if on mobile
    if (this._config.autoShow) {
      const deviceManager = MobileDeviceManager.getInstance();
      const settings = MobileSettings.getInstance();
      this.container.visible = deviceManager.hasTouch && settings.mobileControlsEnabled;
    }
  }

  /**
   * Stop mobile controls
   */
  stop(): void {
    if (!this._active || !this._canvas) return;

    this._active = false;

    // Stop listening for touch events
    this._joystick.stop(this._canvas);
    this._buttons.stop(this._canvas);

    // Reset state
    this._state.active = false;
    this._state.joystickAngle = 0;
    this._state.joystickForce = 0;
    this._state.sprinting = false;
    this._state.crawling = false;

    this.container.visible = false;
    this._canvas = null;
  }

  /**
   * Update mobile controls (call each frame)
   */
  update(dt: number): void {
    if (!this._active) return;

    this._joystick.update(dt);
    this._buttons.update(dt);
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, height: number): void {
    this._joystick.onResize(width, height);
    this._buttons.onResize(width, height);
  }

  /**
   * Set state change callback
   */
  onStateChange(callback: (state: MobileInputState) => void): void {
    this._onStateChange = callback;
  }

  /**
   * Get current state
   */
  getState(): MobileInputState {
    return { ...this._state };
  }

  /**
   * Get joystick angle
   */
  getJoystickAngle(): number {
    return this._state.joystickAngle;
  }

  /**
   * Get joystick force
   */
  getJoystickForce(): number {
    return this._state.joystickForce;
  }

  /**
   * Check if joystick is active
   */
  isJoystickActive(): boolean {
    return this._joystick.isActive();
  }

  /**
   * Check if sprinting
   */
  isSprinting(): boolean {
    return this._state.sprinting;
  }

  /**
   * Check if crawling
   */
  isCrawling(): boolean {
    return this._state.crawling;
  }

  /**
   * Check if controls are active
   */
  isActive(): boolean {
    return this._active;
  }

  /**
   * Show/hide controls
   */
  setVisible(visible: boolean): void {
    this.container.visible = visible;
    this._joystick.setVisible(visible);
    this._buttons.setVisible(visible);
  }

  /**
   * Check if should show mobile controls
   */
  static shouldShow(): boolean {
    const deviceManager = MobileDeviceManager.getInstance();
    const settings = MobileSettings.getInstance();
    return deviceManager.hasTouch && settings.mobileControlsEnabled;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this._canvas) {
      this.stop();
    }
    this._joystick.destroy();
    this._buttons.destroy();
    this.container.destroy({ children: true });
    this._onStateChange = null;
  }
}
